import { getDate, getTime } from "./date";
import format from "date-fns/format";
import parse from "date-fns/parse";
import { extractHoursAndMinutes } from "./date";

const airportDisplayObject = (value) => {
    if (value == null || value === "") return null;
    if (typeof value === "string") return { longName: value, shortName: value };
    if (typeof value === "object") {
        const longName =
            value.longName ||
            value.name ||
            value.cityName ||
            value.shortName ||
            value.code ||
            value.icao ||
            value.iata ||
            "";
        if (longName) return { ...value, longName: String(longName) };
    }
    return null;
};

/** Maps quote / portal / brief leg shapes onto fields the schedule table expects. */
export const normalizeScheduleLeg = (leg) => {
    if (!leg || typeof leg !== "object") return leg;
    const departureAirport =
        airportDisplayObject(leg.departureAirport) ||
        airportDisplayObject(leg.fromAirport) ||
        airportDisplayObject(leg.departure) ||
        airportDisplayObject(leg.airport_From) ||
        airportDisplayObject(leg.from);
    const arrivalAirport =
        airportDisplayObject(leg.arrivalAirport) ||
        airportDisplayObject(leg.toAirport) ||
        airportDisplayObject(leg.arrival) ||
        airportDisplayObject(leg.airport_To) ||
        airportDisplayObject(leg.to);
    const luggage =
        leg.luggage !== undefined && leg.luggage !== null
            ? leg.luggage
            : (leg.number_of_Bags ?? leg.numberOfBags ?? leg.bags);
    const passengers =
        leg.passengers !== undefined && leg.passengers !== null
            ? leg.passengers
            : (leg.number_of_Passengers ?? leg.numberOfPassengers);
    return {
        ...leg,
        departureAirport: departureAirport || leg.departureAirport || null,
        arrivalAirport: arrivalAirport || leg.arrivalAirport || null,
        ...(luggage !== undefined && luggage !== null ? { luggage } : {}),
        ...(passengers !== undefined && passengers !== null ? { passengers } : {}),
    };
};

/**
 * @param {Array|null} legData - Array of leg objects, or null/undefined
 * @param {object|string|null} departureAirport - Departure airport (object or longName)
 * @param {object|string|null} arrivalAirport - Arrival airport (object or longName)
 * @param {number|string} passengers - Passenger count
 * @param {number|string|undefined|null} journeyLuggage - Selected luggage when synthesising a single leg row (not aircraft max)
 */
export const getLegsToRender = (legData, departureAirport, arrivalAirport, passengers, journeyLuggage) =>
    legData && legData.length > 0
        ? legData.map((leg) => normalizeScheduleLeg(leg))
        : [
              normalizeScheduleLeg({
                  departureAirport,
                  arrivalAirport,
                  passengers,
                  luggage: journeyLuggage,
              }),
          ];

/**
 * @param {string|number} dur - Duration (e.g. "1:30" or number)
 */
export const formatDuration = (dur) => {
    if (!dur) return "-";
    if (typeof dur === "string" && dur.includes(":")) {
        try {
            return extractHoursAndMinutes(dur);
        } catch (e) {
            return String(dur);
        }
    }
    return String(dur);
};

/**
 * @param {string} legDepartureTime - Departure time string for the leg
 */
export const formatLegDate = (legDepartureTime) => {
    if (!legDepartureTime) return "-";
    try {
        const departureDate = getDate(legDepartureTime);
        const parseDeparture = parse(departureDate, "yyyy-MM-dd", new Date());
        return format(parseDeparture, "dd/MM/yy", { awareOfUnicodeTokens: true });
    } catch (e) {
        return "-";
    }
};

/**
 * @param {string} value - Date-time ("dd/MM/yyyy HH:mm") or time-only ("HH:mm")
 * @returns {string}
 */
const formatScheduleTime = (value) => {
    if (!value) return "-";
    const parsedTime = getTime(value);
    if (parsedTime) return parsedTime;
    if (typeof value === "string" && value.includes(":")) return value;
    return "-";
};

const normalizeScheduleTimeCandidate = (value) => {
    if (value == null) return null;
    if (typeof value === "object") return null;
    const s = String(value).trim();
    return s === "" ? null : s;
};

/** Use the first candidate that formats to a real schedule time (avoids unusable leg.* shadowing aircraft fallbacks). */
const pickScheduleTimeValue = (...candidates) => {
    for (const raw of candidates) {
        const c = normalizeScheduleTimeCandidate(raw);
        if (c == null) continue;
        if (formatScheduleTime(c) !== "-") return c;
    }
    return undefined;
};

const getValueForLeg = (value, index) => (Array.isArray(value) ? value[index] : value);

/**
 * @param {object} leg - Leg object (departureTime, arrivalTime, duration, fuelStop, etc.)
 * @param {number} index - Leg index (for array fallbacks)
 * @param {object} props - Trip-level values
 * @param {string|Array} props.departureTime - Departure time(s)
 * @param {string|Array} props.arrivalTime - Arrival time(s)
 * @param {string|number|Array} props.duration - Duration(s)
 * @param {number|Array} props.fuelStop - Fuel stop count(s)
 */
export const getLegDerivedValues = (leg, index, { departureTime, arrivalTime, duration, fuelStop }) => {
    const indexedDepartureTime = getValueForLeg(departureTime, index);
    const indexedArrivalTime = getValueForLeg(arrivalTime, index);
    const legDepartureTime = pickScheduleTimeValue(leg.departureTime, leg.departureDateTime, indexedDepartureTime);
    const legArrivalTime = pickScheduleTimeValue(leg.arrivalTime, leg.arrivalDateTime, indexedArrivalTime);
    const legFuelStop =
        leg.fuelStop !== undefined
            ? leg.fuelStop
            : Array.isArray(fuelStop)
              ? fuelStop[index]
              : fuelStop && index === 0
                ? fuelStop
                : 0;
    const legDuration = leg.duration || getValueForLeg(duration, index);
    const departureTimeFormatted = formatScheduleTime(legDepartureTime);
    const arrivalTimeFormatted = formatScheduleTime(legArrivalTime);
    const legDepartureDate = getDate(legDepartureTime) || leg.departureDate;
    const legArrivalDate = getDate(legArrivalTime) || leg.arrivalDate;

    return {
        legDepartureTime,
        legArrivalTime,
        legDepartureDate,
        legArrivalDate,
        formattedDepartureDate: formatLegDate(legDepartureTime),
        departureTimeFormatted,
        arrivalTimeFormatted,
        legFuelStop,
        legDuration,
    };
};
